import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError, notEmpty } from 'src/utility/utils';
import { setToNewZealandTimeZone, todayJsDate, toDateTime } from 'src/utility/helper/dateHelper';
import { FishingTripPeriodRequest, FishingTripPeriodResponse, FishingTripPeriodsClient } from 'src/utility/apis/crewPayApiClient';
import { stringResources } from 'src/assets/config/stringResources';

export interface FishingTripPeriod extends FishingTripPeriodResponse { }

export interface FishingTripPeriodModal extends FishingTripPeriodRequest {
    fishingTripPeriodId: number;
}

export interface State {
    isLoading: boolean;
    fishingTripPeriodList: FishingTripPeriod[];
    error: boolean;
}

const initialState: State = {
    isLoading: false,
    fishingTripPeriodList: [],
    error: false,
};

const tripPeriodSlice = createSlice({
    name: 'tripPeriod',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getFishingTripPeriodListSuccess: (state, action: PayloadAction<FishingTripPeriod[]>) => {
            state.fishingTripPeriodList = action.payload;
            state.isLoading = false;
        },
    },
});

// Reducer
export default tripPeriodSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getFishingTripPeriodListSuccess,
} = tripPeriodSlice.actions;

// for useSelector
export const selectTripPeriod = (state: RootState) => state.tripPeriod as State;

export function getAllTripPeriod(fishingTripId: number) {
    return async (dispatch, getState) => {
        dispatch(tripPeriodSlice.actions.startLoading());
        try {
            const client = new FishingTripPeriodsClient(undefined, axios);
            const response = await client.getByFishingTrip(fishingTripId);
            const sortedResponse = response.sort((a, b) => {
                const aNumber = Number((a.category ?? '').replace('Period ', ''));
                const bNumber = Number((b.category ?? '').replace('Period ', ''));
                return aNumber - bNumber;
            })
            dispatch(tripPeriodSlice.actions.getFishingTripPeriodListSuccess(sortedResponse));
            return sortedResponse;
        } catch (error) {
            dispatch(tripPeriodSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createFishingTripPeriod(fishingTripPeriod: FishingTripPeriodModal, hideNotification = false) {
    return async (dispatch, getState) => {
        dispatch(tripPeriodSlice.actions.startLoading());
        try {
            const { fishingTripPeriodId, ...payload } = fishingTripPeriod;
            const request = payload as FishingTripPeriodRequest;
            request.fishingTripPeriodStartDate = setToNewZealandTimeZone(request.fishingTripPeriodStartDate);
            if (request.fishingTripPeriodEndDate) {
                request.fishingTripPeriodEndDate = setToNewZealandTimeZone(request.fishingTripPeriodEndDate);
            }
            const client = new FishingTripPeriodsClient(undefined, axios);
            const response = await client.create(request);
            if (!!!hideNotification) {
                toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
            }
            return response;
        } catch (error) {
            dispatch(tripPeriodSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateFishingTripPeriod(fishingTripPeriod: FishingTripPeriodModal) {
    return async (dispatch, getState) => {
        dispatch(tripPeriodSlice.actions.startLoading());
        try {
            const { fishingTripPeriodId, ...payload } = fishingTripPeriod;
            const request = payload as FishingTripPeriodRequest;
            request.fishingTripPeriodStartDate = setToNewZealandTimeZone(request.fishingTripPeriodStartDate);
            if (request.fishingTripPeriodEndDate) {
                request.fishingTripPeriodEndDate = setToNewZealandTimeZone(request.fishingTripPeriodEndDate);
            }
            const client = new FishingTripPeriodsClient(undefined, axios);
            await client.edit(fishingTripPeriodId, request);
            toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
        } catch (error) {
            dispatch(tripPeriodSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deleteFishingTripPeriod(fishingTripPeriodId: number) {
    return async (dispatch, getState) => {
        try {
            const client = new FishingTripPeriodsClient(undefined, axios);
            await client.delete(fishingTripPeriodId);
            toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
        } catch (error) {
            dispatch(tripPeriodSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function getDefaultFishingTripPeriodStartDate(fishingTripPeriodList: FishingTripPeriod[]) {
    const fishingTripPeriodEndDateList = fishingTripPeriodList.map(p => p.fishingTripPeriodEndDate).filter(notEmpty);
    const listLength = fishingTripPeriodEndDateList.length;
    return listLength > 0
        ? toDateTime(fishingTripPeriodEndDateList[listLength - 1]).plus({ day: 1 }).toJSDate()
        : todayJsDate();
}

export async function getTripPeriod(fishingTripPeriodId: number) {
    const client = new FishingTripPeriodsClient(undefined, axios);
    return await client.get(fishingTripPeriodId);
}